<template>
  <div class="columns is-vcentered is-size-6">
    <div class="column is-2 has-text-centered">
      <template v-if="isOrderItemEditable">
        <v-popover placement="left" offset="2" tabindex="-1" :disabled="! isOrderItemEditable">
          <b-field 
            :type="getFieldTypesFromValidationPath('orderItem.item_code')"
            :message="getFieldMessagesFromValidationPath('orderItem.item_code')">
              <b-input type="text" name="item_code" placeholder="Code" v-model.lazy.trim="itemCode" customClass="has-text-centered" autocomplete="off" :disabled="! isOrderItemEditable" @blur="createOrReplaceManually(itemCode)" v-tooltip.top="itemCodeTooltipOptions"></b-input>
          </b-field>
          <template slot="popover">
            <section class="item-picker">
              <template v-if="itemFilterGroupBreadcrumbs.length || ! currentItemFilterGroupId">
                <h6 class="is-title is-size-6">Selected Filters:</h6>
                <br>
                <nav class="breadcrumb has-arrow-separator is-size-7" aria-label="breadcrumbs">
                  <ul>
                    <li v-if="! itemFilterGroupBreadcrumbs.length && ! currentItemFilterGroupId"><a href="#" @click="resetSearch"><i class="sl-cross"></i> Search Results</a></li>
                    <li v-for="itemFilterBreadcrumb in itemFilterGroupBreadcrumbs" :key="itemFilterBreadcrumb.itemFilterGroup.id"><a href="#" @click="updateCurrentItemFilterGroupId(itemFilterBreadcrumb.itemFilterGroup)"><i class="sl-cross"></i> {{ itemFilterBreadcrumb.itemFilter.name }}</a></li>
                  </ul>
                </nav>
              </template>
              <b-field grouped group-multiline>
                <p v-for="itemFilter in itemFilters" :key="itemFilter.id" class="control">
                  <b-button size="is-small" @click="updateCurrentItemFilterGroupId(getChildItemFilterGroup(itemFilter, itemFilterGroup), itemFilter)">{{ itemFilter.name }}</b-button>
                </p>
              </b-field>
              <b-field>
                <b-input placeholder="Search..." type="search" icon="magnify" size="is-small" expanded v-model="query" @input="search" />
              </b-field>
              <b-table :data="filteredSearchableData" :striped="true" :narrowed="true" :hoverable="true" :focusable="true" :mobile-cards="true" :selected="selectedItem" @select="createOrReplace" class="item-picker-filter-results is-size-7" :row-class="() => 'is-clickable'"> 
                  <b-table-column v-slot="props" field="item_name" label="Name" sortable v-close-popover>
                    {{ props.row.item_name }} ({{ props.row.item_code }})
                  </b-table-column>
                  <!-- <b-table-column v-slot="props" field="price" label="Price" sortable v-close-popover>
                    {{ calculatePrice(props.row) | currency }}
                  </b-table-column> -->
                <template slot="empty">
                  <section class="section">
                    <div class="content has-text-grey has-text-centered">
                      <p>
                        <b-icon icon="emoticon-sad" size="is-large"></b-icon>
                      </p>
                      <p>Nothing here.</p>
                    </div>
                  </section>
                </template>      
              </b-table>
            </section>
          </template>
        </v-popover>
      </template>
      <template v-else-if="hasSelectedItem">
        {{ orderItem.item_code }}
      </template>
    </div>
    <div class="column is-8">
      <template v-if="orderItem.item_name">
        {{ orderItem.item_name }}
      </template>
      <template v-else>
        <span class="has-text-grey-light">Select Item...</span>
      </template>
    </div>
    <!--
    <div class="column is-1 has-text-centered">
      <template v-if="hasSelectedItem">
        {{ orderItem.price | currency }}
      </template>
    </div>
    -->
    <div class="column is-2 has-text-centered is-tabular-numbers">
      <template v-if="isOrderItemEditable && hasSelectedItem">
      <b-field
        :type="getFieldTypesFromValidationPath('orderItem.quantity')"
        :message="getFieldMessagesFromValidationPath('orderItem.quantity')"
        grouped>
        <b-input
          type="number" 
          id="quantity" 
          name="quantity" 
          placeholder="Qty"
          :min="minQuantity"
          :max="maxQuantity"
          v-model.trim="orderItem.quantity" 
          :custom-class="$v.orderItem.quantity.$invalid ? 'is-danger' : 'is-primary'"
          customClass="has-text-centered"
          @focus="selectText"
          @change.native="updateQuantity"
          v-tooltip.top="quantityTooltipOptions"
          expanded>
        </b-input>
        <b-button type="is-danger" icon-right="delete" @click.native="remove" />
      </b-field>
      </template>
      <template v-else-if="hasSelectedItem">
        {{ orderItem.quantity }}
      </template>
    </div>
    <!--
    <div class="column is-1 has-text-right is-tabular-numbers">
      <template v-if="hasSelectedItem">
        {{ orderItem.subtotal | currency }}
      </template>
    </div>
    -->
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { required, integer, maxLength } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import Fuse from 'fuse.js'
import uuid from 'uuid/v1'
import { currency } from '@/filters'
import { validations, messages } from '@/plugins/vuelidate/validations/order-item'
import { defaultAmountShipping, federalExciseTaxRate } from '@/config'
import orderMixin from '@/mixins/order'

// import { caseCounts } from '@/plugins/vuelidate/validators'

export default {
  mixins: [
    orderMixin
  ],
  props: {
    index: {
      type: Number,
      default: null
    },
    orderItem: {
      type: Object,
      default: () => {
        return {
          id: null,
          uuid: uuid(),
          item_code: '',
          item_name: '',
          price: 0,
          quantity: 0
        }
      }
    },
    selectedItem: {
      type: Object,
      default: () => {
        return {
          id: null,
          item_code: '',
          item_name: '',
          price: 0,
          quantity: 0
        }
      }
    }
  },
  data: () => {
    return {
      messages: messages,
      itemCode: '',
      item: {},
      // selectedItem: {},
      currentItemFilterGroupId: 1,
      itemFilterGroupBreadcrumbs: [],
      searchableData: [],
      filteredSearchableData: [],
      query: '',
      searchableKeys: [
        'item_code',
        'item_name'
      ],
      previousItemCode: '',
      previousQuantity: 0,
      hasSuccess: false
    }
  },
  filters: {
    currency
  },
  validations: validations,
  computed: {
    ...mapGetters([
      'customer',
      'order',
      'items',
      'itemTags',
      'itemFilterGroups',
      'priceDiscountGroupsToPriceDiscountLevels'
    ]),
    activeItems() {
      return this.items.filter((item) => item.is_active && item.is_display_pricing_sheet)
    },
    isQuantityEditable () {
      return this.isOrderItemEditable && this.hasOrderItem && this.itemRequiresCaseCounts(this.orderItem.item_code)
    },
    isSaving () {
      return this.$wait.is(['creating order', 'updating order'])
    },
    hasSelectedItem () {
      return this.selectedItem && this.selectedItem.hasOwnProperty('id') && this.selectedItem.id
    },
    hasItem () {
      return this.orderItem.item_code ? true : false
    },
    hasOrderItem () {
      return this.orderItem.id ? true : false
    },
    caseCounts () {
      let caseCounts = [15, 25, 30]
      for (let i = 40; i <= 7500; i += 5) {
          caseCounts.push(i)
      }
      return caseCounts
    },
    minQuantity () {
      return (this.hasItem && this.itemRequiresCaseCounts(this.orderItem.item_code)) ? this.caseCounts[0] : 1
    },
    maxQuantity () {
      return (this.hasItem && this.itemRequiresCaseCounts(this.orderItem.item_code)) ? this.caseCounts[this.caseCounts.length - 1] : 1000
    },
    itemFilterGroup () {
      return this.getItemFilterGroupById(this.currentItemFilterGroupId)
    },
    itemFilters () {
      return (this.itemFilterGroup) ? this.itemFilterGroup.itemFilters : null
    },
    itemFilterGroupBreadcrumbsItemFilters () {
      return this.itemFilterGroupBreadcrumbs.map(itemFilterGroupBreadcrumb => itemFilterGroupBreadcrumb.itemFilter)
    },
    noResults () {
        return this.filteredSearchableData.length === 0
    },
    isShippingInternational () {
      return this.order.shippingAddress.country != 'US'
    },
    updateQuantityLoader () {
      return `updating order item quantity (${ this.uuid || '' })`
    },
    replaceLoader () {
      return `replacing order item (${ this.uuid || '' })`
    },
    createLoader () {
      return `creating order item (${ this.uuid || '' })`
    },
    itemCodeTooltipOptions () {
      return {
        trigger: 'hover',
        content: (! this.hasItem) ? `Select an item to add to your product request` : ``
      }
    },
    quantityTooltipOptions () {
      return {
        trigger: 'hover',
        content: (this.hasItem && this.itemRequiresCaseCounts(this.orderItem.item_code)) ? `This item's quantity is limited to our case sizes` : ``
      }
    },
    hasErrors () {
      return this.$v.orderItem.$error
    }
  },
  methods: {
    ...mapActions([
      'updateOrder',
    ]),
    getFieldTypesFromValidationPath (validationPath) {
      return {
        'is-danger': eval(`this.$v.${ validationPath }.$error`)
      }
    },
    getFieldMessagesFromValidationPath (validationPath) {
      let validationPathArray = validationPath.split('.')
      let messages = this.messages

      while (validationPathArray.length) {
        messages = messages[validationPathArray.shift()]
      }

      let fieldMessages = {}

      for (const validation in messages) {
        fieldMessages[messages[validation]] = eval(`this.$v.${ validationPath }.$error && ! this.$v.${ validationPath }.${ validation }`)
      }

      return fieldMessages
    },
    orderExists () {
      return (this.order.id) ? true : false
    },
    getOrderItemById (id) {
      return this.order.orderItems.find((orderItem) => orderItem.id === id)
    },
    getItemByItemCode (itemCode, activeItemsOnly = false) {
      return (activeItemsOnly) ? this.activeItems.find((item) => item.item_code === itemCode) : this.items.find((item) => item.item_code === itemCode)
    },
    getItemTagById (id) {
      return this.itemTags.find((itemTag) => itemTag.id === id)
    },
    getItemTagByName (itemTagName) {
      return this.itemTags.find((itemTag) => itemTag.item_tag_name === itemTagName)
    },
    isLastIndex (a, i) {
      return i === a.length - 1
    },
    orderHasItem (item) {
      return this.order.orderItems.find((orderItem) => orderItem.item_code === item.item_code)
    },
    itemRequiresCaseCounts (itemCode) {
      let item = this.getItemByItemCode(itemCode)
      if (! item) {
        return
      }
      return item.itemTags.find((itemTag) => itemTag.item_tag_name === 'Case Count Required Dart') ? true : false
    },
    getItemFilterGroupById (id) {
      return this.itemFilterGroups.find(itemFilterGroup => itemFilterGroup.id === id) || null
    },
    getChildItemFilterGroup (itemFilter = null, itemFilterGroup = null) {
      return (itemFilterGroup) ? itemFilterGroup.itemFiltersToItemFilterGroups.find(itemFilterToItemFilterGroup => itemFilterToItemFilterGroup.item_filter_id === itemFilter.id && itemFilterToItemFilterGroup.item_filter_group_id === itemFilterGroup.id).childItemFilterGroup : null
    },
    getPriceDiscountGroupToPriceDiscountLevelByIds (priceDiscountGroupId, priceDiscountLevelId) {
      return this.priceDiscountGroupsToPriceDiscountLevels.find(priceDiscountGroupToPriceDiscountLevel => priceDiscountGroupToPriceDiscountLevel.price_discount_group_id === priceDiscountGroupId && priceDiscountGroupToPriceDiscountLevel.price_discount_level_id === priceDiscountLevelId) || null
    },
    updateIsItemPickerActive (isItemPickerActive) {
      this.isItemPickerActive = isItemPickerActive
    },
    async updateQuery (query) {
      this.query = query
      await this.search()
    },
    itemFilterGroupBreadcrumbClasses () {
      return {
        'is-active': this.isLastIndex()
      }
    },
    updateCurrentItemFilterGroupId (itemFilterGroup = null, itemFilter = null) {
      this.updateItemFilterGroupBreadcrumbs(itemFilter, itemFilterGroup)
        .then(() => this.filter())
        .then(() => this.currentItemFilterGroupId = (itemFilterGroup) ? itemFilterGroup.id : null)
    },
    async updateItemFilterGroupBreadcrumbs (itemFilter = null, itemFilterGroup = null) {
      if (itemFilter) { // Append to breadcrumbs
        this.itemFilterGroupBreadcrumbs.push({
          'itemFilterGroup': this.itemFilterGroup,
          'itemFilter': itemFilter
        })
      } else { // Jump to provided breadcrumb
        const index = this.itemFilterGroupBreadcrumbs.findIndex(itemFilterGroupBreadcrumb => (itemFilterGroup) ? itemFilterGroupBreadcrumb.itemFilterGroup.id === itemFilterGroup.id : false)
       if (index >= 0) {
          this.itemFilterGroupBreadcrumbs.splice(index) // Remove breadcrumbs after provided itemFilterGroup
        } else {
          this.itemFilterGroupBreadcrumbs = [] // Provided itemFilterGroup not found, clear breadcrumbs
        }
      }
    },
    async filter () {
      return this.itemFilterFilter(this.searchableData, this.itemFilterGroupBreadcrumbsItemFilters)
        .then((filteredSearchableData) => this.queryFilter(filteredSearchableData))
        .then((filteredSearchableData) => this.filteredSearchableData = filteredSearchableData)
    },
    async itemFilterFilter (filteredSearchableData, itemFilters) {
      const itemFiltersItemTagIds = itemFilters.reduce((itemFilterItemTagIds, itemFilter) => {
        itemFilterItemTagIds.push(...itemFilter.itemTags.map(itemTag => itemTag.id))
        return itemFilterItemTagIds
      }, [])
      const result = filteredSearchableData.filter((item) => {
        const itemItemTagIds = item.itemTags.map(itemTag => itemTag.id)
        const result = itemFiltersItemTagIds.every((itemFiltersItemTagId) => {
          const result = itemItemTagIds.includes(itemFiltersItemTagId)
          return result
        })
        return result
      })
      return Promise.resolve(result)
    },
    resetSearch () {
      this.query = ''
      this.filteredSearchableData = this.items
      this.currentItemFilterGroupId = 1
    },
    async search () {
      this.updateCurrentItemFilterGroupId()
      return await this.queryFilter(this.searchableData)
        .then((filteredSearchableData) => this.filteredSearchableData = filteredSearchableData)
    },
    // search: debounce(function () {
    //   console.log('debounced')
    //   this.updateCurrentItemFilterGroupId()
    //   return this.queryFilter(this.searchableData)
    //     .then((filteredSearchableData) => this.filteredSearchableData = filteredSearchableData)
    // }, 100),
    async queryFilter (filteredSearchableData) {
      if (! this.query.trim()) {
        return Promise.resolve(filteredSearchableData)
      }
      let fuse = new Fuse(filteredSearchableData, {
        shouldSort: false,
        tokenize: true,
        matchAllTokens: true,
        findAllMatches: true,
        threshold: 0.1,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: this.searchableKeys
      })
      return Promise.resolve(fuse.search(this.query))
    },
    // async paginationFilter () {
    //   this.displayedSearchableData = (this.pageNumber &c& this.pageSize) ? this.filteredSearchableData : this.filteredSearchableData.slice((this.pageNumber - 1) * this.pageSize, this.pageNumber * this.pageSize)
    // },
    // getPriceDiscountGroupToPriceDiscountLevelForItem (item) {
    //   return this.getPriceDiscountGroupToPriceDiscountLevelByIds(item.price_discount_group_id, this.order.price_discount_level_id)
    // },
    // calculatePrice (item) {
    //   // console.log(item)
    //   if (this.flatDiscount200.includes(item.item_code)) {
    //     var price = (Math.ceil((item.price - 200) * 100) / 100).toFixed(2)
    //   } else if (this.flatDiscount300.includes(item.item_code)) {
    //     var price = (Math.ceil((item.price - 300) * 100) / 100).toFixed(2)
    //   } else {
    //     let priceDiscountGroupToPriceDiscountLevel = this.getPriceDiscountGroupToPriceDiscountLevelForItem(item)
    //     // console.log(priceDiscountGroupToPriceDiscountLevel)
    //     var price = parseFloat((Math.ceil((item.price - (item.price * priceDiscountGroupToPriceDiscountLevel.rate)) * 100) / 100).toFixed(2))
    //   }
    //   // console.log(item.item_code, item.price, price)
    //   return price
    // },
    // calculateSubtotal (item, quantity) {
    //   return this.calculatePrice(item) * quantity
    // },
    // calculateSalesTaxSubtotal (item, quantity) {
    //   return (order.shipping_state !== 'PA' || ! item.is_sales_taxable || this.order.is_sales_tax_exempt) ? 0.00 : parseFloat((Math.ceil((calculateSubtotal(item, quantity) * .06) * 100) / 100).toFixed(2))
    // },
    // calculateFederalExciseTaxSubtotal (item, quantity) {
    //   return (! item.is_federal_excise_taxable || this.order.is_federal_excise_tax_exempt) ? 0.00 : parseFloat((Math.ceil((this.calculateFederalExciseTax(item) * quantity) * 100) / 100).toFixed(2))
    // },
    // calculateFederalExciseTax (item, priceDiscountGroupToPriceDiscountLevel) {
    //   let discountedOpticsPrice = (Math.ceil((item.optics_price - (item.optics_price * priceDiscountGroupToPriceDiscountLevel.rate)) * 100) / 100).toFixed(4)
    //   let priceLessDiscountedOptics = (Math.ceil((this.calculatePrice(item) - discountedOpticsPrice) * 100) / 100).toFixed(4)
    //   let priceLessDiscountedOpticsLessCost = (Math.ceil((priceLessDiscountedOptics - item.cost) * 100) / 100).toFixed(4)
    //   let priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRate = (Math.ceil((priceLessDiscountedOpticsLessCost * federalExciseTaxRate) * 100) / 100).toFixed(4)
    //   let priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRateTimesFactor = (Math.ceil((priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRate * 100) * 100) / 100).toFixed(4)
    //   let federalExciseTax = (Math.ceil((priceLessDiscountedOpticsLessCostTimesFederalExciseTaxRateTimesFactor / 100) * 100) / 100).toFixed(2)
    //   return parseFloat(federalExciseTax)
    // },
    itemIsDifferent (item) {
      return this.orderItem.item_code !== item.item_code
    },
    itemAlreadyExistsInOrder (item) {
      const currentOrderItem = this.orderItem
      return (this.order.orderItems.find(function (orderItem) {
        return orderItem.uuid !== currentOrderItem.uuid && orderItem.item_code === item.item_code
      })) ? true : false
    },
    createOrReplaceManually(itemCode) {
      let item = this.getItemByItemCode(itemCode, true)
      if (itemCode && ! item) {
        this.$buefy.dialog.alert({
          type: 'is-danger',
          message: 'Invalid item Code'
        })
        this.itemCode = this.previousItemCode
        return
      }
      return this.createOrReplace(item)
    },
    createOrReplace (item) {
      if (! item) {
        return
      }
      if (this.itemAlreadyExistsInOrder(item)) {
        this.$buefy.dialog.alert('This item already exists in the product request.')
        return
      }
      if (this.hasItem) {
        this.replace(item)
      } else {
        this.create(item)
      }
      // this.selectedItem = item
      // console.log(this.selectedItem)
    },
    create (item) {
      this.$wait.start(this.createLoader)
      let orderItems = this.order.orderItems
      let qty = this.itemRequiresCaseCounts(item.item_code) ? this.caseCounts[0] : 1
      let orderItem = this.initOrderItem(item, qty)
      orderItems.push(orderItem)
      this.updateOrderItems(orderItems)
      this.itemCode = orderItem.item_code
      this.$vnode.key = uuid() // Reset key to re-mount the component for next entry
      this.$wait.end(this.createLoader)
    },
    replace (item) {
      if (this.itemIsDifferent(item) && ! this.orderHasItem(item)) {
        this.$wait.start(this.replaceLoader)
        let orderItems = this.order.orderItems
        let qty = this.itemRequiresCaseCounts(item.item_code) ? this.caseCounts[0] : 1
        let orderItem = this.initOrderItem(item, qty)
        orderItems.splice(this.index, 1, orderItem)
        this.updateOrderItems(orderItems)
        this.itemCode = orderItem.item_code
        this.previousItemCode = orderItem.item_code
        this.previousQuantity = orderItem.quantity  
        this.$wait.end(this.replaceLoader)
      }
    },
    selectText (e) {
      e.target.select()
    },
    findNextCaseCount (quantity, direction) {
      let index = this.caseCounts.findIndex((val, index) => val === quantity)
      // console.log(index)
      if (index === -1) {
        let caseCounts = [...this.caseCounts, quantity]
        caseCounts.sort((a, b) => a - b)
        index = caseCounts.indexOf(quantity)
        if (direction === 'desc') {
          return this.caseCounts[index - 1] || this.caseCounts[0]
        } else {
          return this.caseCounts[index]
        }
      } else {
        return this.caseCounts[index]
      }
    },
    updateQuantity (e) {
      if (isNaN(parseInt(e.target.value))) {
        this.orderItem.quantity = this.previousQuantity
        return
      }
      let quantity = parseInt(e.target.value)
      // console.log(e.target.value, quantity)
      this.$wait.start(this.updateQuantityLoader)
      let orderItems = this.order.orderItems
      let item = this.getItemFromOrderItem(this.orderItem)
      quantity = (this.itemRequiresCaseCounts(this.orderItem.item_code)) ? this.findNextCaseCount(quantity, (quantity > this.previousQuantity) ? 'asc' : 'desc') : quantity
      let orderItem = this.initOrderItem(item, quantity)
      orderItems.splice(this.index, 1, orderItem)
      this.$set(this.order, 'orderItems', orderItems)
      this.updateOrderItems(orderItems)
      this.previousItemCode = orderItem.item_code
      this.previousQuantity = orderItem.quantity
      this.$wait.end(this.updateQuantityLoader)
    },
    remove (e) {
      this.hasSuccess = false
      if (! this.isSaving) {
        this.$buefy.dialog.confirm({
          message: `<p class="has-text-weight-bold">${ this.orderItem.item_name }</p><br><p>Are you sure you want to remove this item?<p>`,
          onConfirm: async () => {
            let orderItems = this.order.orderItems
            orderItems.splice(this.index, 1)
            this.updateOrderItems(orderItems)
          }
        })
      }
    },
    updateOrderItems (orderItems) {
      this.$set(this.order, 'orderItems', orderItems)
      this.applyOrderCalculations()
    }
  },
  beforeMount: function () {
    if (this.hasItem) {
      this.$set(this.orderItem, 'uuid', uuid())
      this.itemCode = this.item.item_code
      this.item = this.getItemByItemCode(this.orderItem.item_code)
      this.previousItemCode = this.orderItem.item_code
      this.previousQuantity = this.orderItem.quantity
    } else {
      this.itemFilterGroupBreadcrumbs = []
    }
    this.searchableData = this.activeItems
    this.filteredSearchableData = this.activeItems
    // this.query = this.item.item_code || ''
    this.$v.$reset()
  },
  mounted: function () {
    this.$nextTick(function () {
      if (this.hasItem) {
        this.$set(this.orderItem, 'uuid', uuid())
        this.itemCode = this.item.item_code
        this.item = this.getItemByItemCode(this.orderItem.item_code)
        this.previousItemCode = this.orderItem.item_code
        this.previousQuantity = this.orderItem.quantity
      } else {
        this.itemFilterGroupBreadcrumbs = []
      }
      this.searchableData = this.activeItems
      this.filteredSearchableData = this.activeItems
      // this.query = this.item.item_code || ''
      this.$v.$reset()
    })
  }
}
</script>
